/** Marker for the collapsed-pages gap rendered as an ellipsis. */
export const ELLIPSIS = 'ellipsis' as const;

export type PaginationSlot = number | typeof ELLIPSIS;

/**
 * Maximum number of page numbers rendered at once, **including** the pinned
 * first and last pages.
 *
 * Matches `INITIAL_PREFETCH_PAGES` in `useNamespaceList` so every page fetched
 * up-front is directly reachable.
 */
export const VISIBLE_PAGE_COUNT = 5;

/** Pages rendered around the current page, between the two pinned ends. */
const CENTERED_RUN = 3;

/**
 * Builds the slot list for a compact pager that renders at most
 * {@link VISIBLE_PAGE_COUNT} page numbers in total.
 *
 * Cloudscape's own `Pagination` hardcodes a 7-control window
 * (`numberOfControls = 7` in its `getPaginationState`) with no prop to change
 * it, which is too wide for the namespace sidebar — hence this local window
 * calculation.
 *
 * Shape: `1 … {current-1} {current} {current+1} … {last}` — the first and last
 * pages are always reachable and the current page sits centred between them,
 * e.g. `1 … 495 496 497 … 500`. Each {@link ELLIPSIS} is emitted only when it
 * actually conceals pages, so near either end the run is contiguous
 * (`1 2 3 4 … 500`, `1 … 497 498 499 500`).
 *
 * NOTE: the pinned right-hand page is the last page *known so far*. The
 * sidebar's pagination is token-based, so `pagesCount` is discovered
 * progressively (see `INITIAL_PREFETCH_PAGES`) and is only the true total once
 * every page has been walked.
 *
 * @param currentPageIndex 1-based index of the active page.
 * @param pagesCount total number of pages known so far (>= 1).
 */
export function buildPaginationWindow(
  currentPageIndex: number,
  pagesCount: number,
): PaginationSlot[] {
  const total = Math.max(1, Math.floor(pagesCount));
  const current = Math.min(Math.max(1, Math.floor(currentPageIndex)), total);

  // Everything fits — render every page, no gaps.
  if (total <= VISIBLE_PAGE_COUNT) {
    return rangeInclusive(1, total);
  }

  // Centre a CENTERED_RUN-long run on the current page, clamped to the
  // interior so it never collides with the pinned first/last pages.
  const lastInterior = total - 1;
  let start = current - Math.floor(CENTERED_RUN / 2);
  start = Math.max(2, Math.min(start, lastInterior - CENTERED_RUN + 1));
  const end = start + CENTERED_RUN - 1;

  const slots: PaginationSlot[] = [1];
  // Only emit a gap where it hides at least one page.
  if (start > 2) {
    slots.push(ELLIPSIS);
  }
  slots.push(...rangeInclusive(start, end));
  if (end < lastInterior) {
    slots.push(ELLIPSIS);
  }
  slots.push(total);

  return slots;
}

function rangeInclusive(from: number, to: number): number[] {
  const result: number[] = [];
  for (let i = from; i <= to; i += 1) {
    result.push(i);
  }
  return result;
}
